import { DischargeDisposition, DischargePatient } from '~/types/discharge';

export interface PlaceProviderMatch {
  id: string;
  name: string;
  distanceMiles: number;
  capacity: 'high' | 'medium' | 'low';
  clinicalFit: number; // 0-100
  qualityRating: number; // 0-5
  totalScore: number;
  fitLabel: 'Best Fit' | 'Strong Option' | 'Backup';
  preferredPartner?: boolean;
}

const PROVIDER_POOL: Record<DischargeDisposition, string[]> = {
  home_health:      ['Metropolitan Home Health', 'Sunrise Home Health', 'CareFirst Home Services', 'Eastside Home Health'],
  hospital_at_home: ['Metropolitan Hospital at Home', 'Coastal Care Partners', 'Harbor Health at Home'],
  snf_at_home:      ['Northside SNF Care', 'Greenfield SNF at Home', 'Liberty Home Care'],
  hospice:          ['Comfort Care Hospice', 'Wellbridge Palliative', 'Community Palliative Care'],
  infusion_at_home: ['Advanced Home Infusion', 'Apex Infusion Therapy', 'Premier Wound Care Services'],
  palliative:       ['Wellbridge Palliative', 'Community Palliative Care', 'Beacon Speech & Rehab'],
};

function hashOf(s: string): number {
  return Array.from(s).reduce((acc, c) => acc + c.charCodeAt(0), 0);
}

export function buildPlaceMatches(patient: DischargePatient): PlaceProviderMatch[] {
  const pool = PROVIDER_POOL[patient.disposition] ?? PROVIDER_POOL.home_health;
  const h = hashOf(patient.id);
  return pool.slice(0, 3).map((name, idx) => {
    const totalScore = 92 - idx * 8 - ((h + idx * 5) % 6);
    const fitLabel: PlaceProviderMatch['fitLabel'] =
      idx === 0 ? 'Best Fit' : idx === 1 ? 'Strong Option' : 'Backup';
    return {
      id: `${patient.id}-prov-${idx}`,
      name,
      distanceMiles: 2 + ((h + idx * 11) % 18),
      capacity: idx === 0 ? 'high' : idx === 1 ? 'medium' : 'low',
      clinicalFit: 90 - idx * 12 - ((h + idx * 3) % 6),
      qualityRating: 4.8 - idx * 0.3 - ((h + idx) % 3) * 0.1,
      totalScore,
      fitLabel,
      preferredPartner: idx === 0 && (h % 2 === 0),
    };
  });
}

export function getSelectedProviderName(patient: DischargePatient): string | null {
  if (patient.selectedProviderName) return patient.selectedProviderName;
  const matches = buildPlaceMatches(patient);
  return matches[0]?.name ?? null;
}
